import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  Banknote,
  Send,
  Loader2,
  ShieldAlert,
  Sparkles,
  KeyRound,
  Copy,
  Check as CheckIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import LegalConsent from "@/components/LegalConsent";
import CopyRow from "@/components/CopyRow";
import MethodInstructions from "@/components/MethodInstructions";

const METHODS = [
  { id: "stripe", name: "Tarjeta de débito / crédito", subtitle: "Procesado por Stripe · Instantáneo", Icon: CreditCard, color: "blue", instant: true },
  { id: "western_union", name: "Western Union", subtitle: "Depósito en efectivo", Icon: Banknote, color: "amber", instant: false },
  { id: "money_gram", name: "MoneyGram", subtitle: "Depósito en efectivo", Icon: Banknote, color: "rose", instant: false },
  { id: "remitly", name: "Remitly", subtitle: "App o sitio web", Icon: Send, color: "emerald", instant: false },
];

export default function Pagar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan") || "personal";
  const canceled = searchParams.get("canceled") === "1";

  const [planData, setPlanData] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const [method, setMethod] = useState("stripe");
  const [extraCallType, setExtraCallType] = useState("emergency");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [welcome, setWelcome] = useState(null); // { pin, plan_name, message }
  const [pinCopied, setPinCopied] = useState(false);

  useEffect(() => {
    if (canceled) toast.info("Pago cancelado. Puedes intentarlo de nuevo cuando quieras.");
    api.get("/plans").then((r) => {
      const p = r.data.plans.find((x) => x.id === planId);
      setPlanData(p);
      setBeneficiary(r.data.beneficiary);
    });
  }, [planId, canceled]);

  const copyValue = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(""), 1500);
  };

  const submitStripe = async () => {
    if (!legalAccepted) {
      toast.error("Debes aceptar el Aviso de Privacidad y los Términos y Condiciones.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
        extra_call_type: planId === "extra_call" ? extraCallType : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el pago con tarjeta");
      setSubmitting(false);
    }
  };

  const submitReport = async () => {
    if (!legalAccepted) {
      toast.error("Debes aceptar el Aviso de Privacidad y los Términos y Condiciones.");
      return;
    }
    if (!reference.trim()) {
      toast.error("Escribe la referencia de tu pago para activar tu servicio.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/report", {
        plan_id: planId,
        method,
        reference: reference.trim(),
        notes: notes.trim(),
        extra_call_type: planId === "extra_call" ? extraCallType : null,
      });
      setWelcome({
        pin: data.pin,
        plan_name: data.plan_name,
        message: data.message,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar tu pago");
    } finally {
      setSubmitting(false);
    }
  };

  const copyPin = () => {
    if (!welcome?.pin) return;
    navigator.clipboard.writeText(welcome.pin);
    setPinCopied(true);
    toast.success("PIN copiado");
    setTimeout(() => setPinCopied(false), 1800);
  };

  if (!planData) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const isCash = method === "western_union" || method === "money_gram";
  const isRemitly = method === "remitly";
  const isManual = isCash || isRemitly;
  const selectedMethod = METHODS.find((m) => m.id === method);

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <header className="max-w-3xl mx-auto w-full px-5 pt-6 flex items-center justify-between">
        <button
          onClick={() => navigate("/precios")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900"
          data-testid="pagar-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <BrandMark size="sm" />
      </header>

      <div className="max-w-3xl mx-auto w-full px-5 pt-8">
        {/* Plan summary */}
        <div className="bg-zinc-900 text-white rounded-2xl p-6 mb-6">
          <p className="text-xs uppercase tracking-widest font-black text-zinc-400 mb-1">
            Plan seleccionado
          </p>
          <div className="flex items-end justify-between gap-3">
            <h1 className="text-3xl font-black tracking-tight">{planData.name}</h1>
            <div className="text-right">
              <p className="text-4xl font-black tracking-tighter">
                ${parseFloat(planData.price_usd).toFixed(2)}
              </p>
              <p className="text-xs text-zinc-400 font-bold">USD · pago único</p>
            </div>
          </div>
          <p className="text-sm text-zinc-300 mt-2 leading-snug">{planData.description}</p>

          {planId === "extra_call" && (
            <div className="mt-4 bg-zinc-800 rounded-xl p-3">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">
                ¿Qué tipo de llamada adicional?
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="extra-type-emergency"
                  onClick={() => setExtraCallType("emergency")}
                  className={`h-12 rounded-lg font-bold text-sm transition-all ${
                    extraCallType === "emergency" ? "bg-red-600 text-white" : "bg-zinc-700 text-zinc-300"
                  }`}
                >
                  Emergencia (frente a ICE)
                </button>
                <button
                  data-testid="extra-type-detention"
                  onClick={() => setExtraCallType("detention")}
                  className={`h-12 rounded-lg font-bold text-sm transition-all ${
                    extraCallType === "detention" ? "bg-blue-600 text-white" : "bg-zinc-700 text-zinc-300"
                  }`}
                >
                  Centro de detención
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Method selector */}
        <h2 className="text-lg font-black text-zinc-900 mb-3">Elige cómo vas a pagar</h2>
        <div className="grid grid-cols-2 gap-2 mb-6">
          {METHODS.map((m) => {
            const active = method === m.id;
            const { Icon } = m;
            return (
              <button
                key={m.id}
                data-testid={`pagar-method-${m.id}`}
                onClick={() => setMethod(m.id)}
                className={`text-left rounded-xl p-4 transition-all border-2 ${
                  active ? "border-zinc-900 bg-white shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300"
                }`}
              >
                <Icon className={`w-6 h-6 mb-2 ${active ? "text-zinc-900" : "text-zinc-400"}`} strokeWidth={2.5} />
                <p className="text-sm font-bold text-zinc-900 leading-tight">{m.name}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{m.subtitle}</p>
                {m.instant && active && (
                  <span className="inline-block mt-2 text-[10px] font-black uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    Instantáneo
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Stripe direct payment */}
        {method === "stripe" && (
          <div className="bg-white border-2 border-zinc-200 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <CreditCard className="w-8 h-8 text-blue-700" strokeWidth={2.5} />
              <div>
                <h3 className="font-black text-zinc-900">Pago con tarjeta</h3>
                <p className="text-sm text-zinc-600 leading-snug">
                  Serás redirigido a la pasarela segura de Stripe. Tras el pago, tu plan se activa al instante.
                </p>
              </div>
            </div>
            <LegalConsent checked={legalAccepted} onChange={setLegalAccepted} testId="pagar-legal-consent-stripe" />
            <Button
              data-testid="pagar-stripe-submit"
              onClick={submitStripe}
              disabled={submitting || !legalAccepted}
              className="w-full h-16 text-lg font-black bg-blue-700 hover:bg-blue-800 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Redirigiendo…</>
              ) : (
                <>Pagar ${parseFloat(planData.price_usd).toFixed(2)} USD con tarjeta</>
              )}
            </Button>
          </div>
        )}

        {/* Manual methods (WU / MG / Remitly) */}
        {isManual && beneficiary && (
          <div className="space-y-5">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-5 h-5 text-amber-700" />
                <h3 className="font-black text-zinc-900">Paso 1 · Haz tu depósito a este beneficiario</h3>
              </div>
              <div className="bg-white rounded-xl p-4 mb-3 space-y-2">
                <CopyRow label="Nombre del beneficiario" value={beneficiary.name} copied={copied === "name"} onCopy={() => copyValue("name", beneficiary.name)} testId="copy-beneficiary-name" />
                <CopyRow label="Ciudad" value={beneficiary.city} copied={copied === "city"} onCopy={() => copyValue("city", beneficiary.city)} testId="copy-beneficiary-city" />
                <CopyRow label="Estado" value={beneficiary.state} copied={copied === "state"} onCopy={() => copyValue("state", beneficiary.state)} testId="copy-beneficiary-state" />
                <CopyRow label="País" value={beneficiary.country} copied={copied === "country"} onCopy={() => copyValue("country", beneficiary.country)} testId="copy-beneficiary-country" />
                <CopyRow label="Monto a depositar" value={`$${parseFloat(planData.price_usd).toFixed(2)} USD`} copied={copied === "amount"} onCopy={() => copyValue("amount", String(planData.price_usd))} testId="copy-beneficiary-amount" />
              </div>
              <p className="text-xs text-amber-900 leading-snug">
                Lleva tu identificación con el nombre que usas habitualmente. {selectedMethod?.name} te dará un número MTCN al hacer el depósito.
              </p>
            </div>

            {/* PASO 1.5 — Method-specific detailed instructions */}
            <MethodInstructions method={method} />

            <div className="bg-white border-2 border-zinc-200 rounded-2xl p-6 overflow-hidden relative">
              {/* HERO BANNER: emotional CTA */}
              <div className="relative -mx-6 -mt-6 mb-5 px-6 pt-5 pb-4 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-amber-400/20 rounded-full blur-2xl pointer-events-none" />
                <div className="relative flex items-start gap-3">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-400 flex items-center justify-center shadow-lg shadow-amber-500/40">
                    <Sparkles className="w-5 h-5 text-emerald-950" strokeWidth={2.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300 mb-1">
                      Paso 2 · Activación inmediata
                    </p>
                    <h3 className="text-xl font-black tracking-tight leading-tight">
                      Reporta tu pago para recibir el servicio
                    </h3>
                    <p className="text-xs text-emerald-100 leading-snug mt-1">
                      Apenas registres tu referencia, tu PIN y tu plan se activan al instante.
                      Nuestro equipo verificará el depósito después.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-bold text-zinc-900 mb-1.5 block">
                    Referencia de tu pago <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    data-testid="pagar-reference"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={
                      method === "western_union"
                        ? "MTCN, número de control o cualquier dato del recibo"
                        : method === "money_gram"
                        ? "Número de referencia o folio de MoneyGram"
                        : "ID de transacción o número de Remitly"
                    }
                    className="h-14 text-base font-mono rounded-xl border-2 border-zinc-300 focus:border-emerald-600 uppercase"
                  />
                  <p className="text-xs text-zinc-500 mt-1.5 leading-snug">
                    Escribe <span className="font-bold">cualquier</span> dato del comprobante de {selectedMethod?.name}: número MTCN, folio, código de envío, número de recibo, etc.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-bold text-zinc-900 mb-1.5 block">
                    Notas adicionales (opcional)
                  </Label>
                  <Input
                    data-testid="pagar-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Sucursal, fecha del depósito, etc."
                    className="h-12 text-sm rounded-xl border-2 border-zinc-300 focus:border-emerald-600"
                  />
                </div>
              </div>

              <LegalConsent checked={legalAccepted} onChange={setLegalAccepted} testId="pagar-legal-consent-manual" />
              <Button
                data-testid="pagar-report-submit"
                onClick={submitReport}
                disabled={submitting || !legalAccepted}
                className="w-full h-16 text-lg font-black bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white rounded-xl mt-5 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-600/30"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Activando tu plan…</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> Reportar pago y activar mi servicio</>
                )}
              </Button>

              <p className="text-xs text-zinc-500 text-center mt-3 leading-snug">
                Servicio activo al instante. Si tu referencia no es válida, el equipo de soporte podrá suspender el acceso después.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Welcome modal — shown after a successful report */}
      {welcome && (
        <div
          data-testid="pagar-welcome-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white p-7 text-center">
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-amber-400/25 rounded-full blur-3xl" />
              <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-emerald-300/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-amber-400 mx-auto mb-3 flex items-center justify-center shadow-lg shadow-amber-500/40">
                  <Sparkles className="w-8 h-8 text-emerald-950" strokeWidth={2.8} />
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300 mb-1">
                  Tu plan está activo
                </p>
                <h2 className="text-2xl font-black tracking-tight">¡Bienvenido!</h2>
                <p className="text-sm text-emerald-100 mt-2 leading-snug">
                  Tu plan <span className="font-black text-white">{welcome.plan_name}</span> ya está disponible. Guarda tu PIN — es tu llave para llamar a la IA.
                </p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 text-center">
                Tu PIN de llamadas
              </p>
              <button
                data-testid="pagar-welcome-pin"
                onClick={copyPin}
                className="w-full mb-4 bg-zinc-900 hover:bg-zinc-800 rounded-2xl p-5 text-white flex items-center justify-between transition-all group"
              >
                <div className="flex items-center gap-3">
                  <KeyRound className="w-6 h-6 text-amber-400" strokeWidth={2.5} />
                  <span className="font-mono font-black text-3xl tracking-[0.3em]">
                    {welcome.pin || "—"}
                  </span>
                </div>
                {pinCopied ? (
                  <CheckIcon className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Copy className="w-5 h-5 text-zinc-400 group-hover:text-white" />
                )}
              </button>

              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 mb-5">
                <p className="text-[11px] text-amber-900 leading-snug">
                  <span className="font-black">Importante:</span> Apunta tu PIN o tómale una foto. Es la única forma de entrar a tu cuenta sin tu usuario.
                </p>
              </div>

              <Button
                data-testid="pagar-welcome-go-dashboard"
                onClick={() => navigate("/dashboard")}
                className="w-full h-14 text-base font-black bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl"
              >
                Entrar a mi panel
              </Button>
            </div>
          </div>
        </div>
      )}

      <DisclaimerBanner />
    </div>
  );
}
